import Component from '@glimmer/component';
import { cached } from '@glimmer/tracking';
import { service } from '@ember/service';
import { t, type IntlService } from 'ember-intl';
import Button from 'tio-ui/components/button';
import Chip from 'tio-ui/components/chip';
import { PhArrowLeft, PhArrowSquareOut, PhSealCheck } from 'ember-phosphor-icons';
import { buildLpnCardView, lpnBadges, lpnDescriptionHtml } from 'tio-common/utils/lpn/card-view';
import type { LpnListing } from 'tio-common/utils/lpn/listing';
import { schoolInitials } from 'tio-employee/components/lpn/school-row';
import LpnSchoolLogo from 'tio-employee/components/lpn/school-logo';

interface LpnSchoolDetailSignature {
  Args: {
    /** The school as the viewer's company sees it. */
    listing: LpnListing;
    /** Returns to the school list. */
    onBack: () => void;
    /** Shows a Select button that picks this school; omitted when browsing only. */
    onPick?: (listing: LpnListing) => void;
    /** A pick is in flight, so the Select button is disabled. */
    isPicking?: boolean;
  };
}

/** A school's full description, benefits and site link, shown in place of the drawer list. */
export default class LpnSchoolDetail extends Component<LpnSchoolDetailSignature> {
  @service declare intl: IntlService;

  @cached
  get view() {
    return buildLpnCardView(this.args.listing.page, this.args.listing.setting);
  }

  @cached
  get badges() {
    return lpnBadges(this.view, this.intl);
  }

  get descriptionHtml() {
    return lpnDescriptionHtml(this.args.listing.page, this.args.listing.setting);
  }

  get subtitle() {
    return [this.view.providedBy?.trim(), ...(this.view.format ?? [])].filter(Boolean).join(' · ');
  }

  get initials() {
    return schoolInitials(this.view.schoolName);
  }

  pick = () => this.args.onPick?.(this.args.listing);

  <template>
    <div class="tio-col gap-4" data-test-lpn-school-detail>
      <Button
        class="self-start"
        @appearance="link"
        @size="sm"
        @label={{t "lpn.schools_drawer.back"}}
        @icon={{PhArrowLeft}}
        {{on "click" @onBack}}
        data-test-lpn-school-detail-back
      />
      <LpnSchoolLogo @src={{this.view.imageUrl}} @initials={{this.initials}} @size="banner" />
      <div class="tio-row-top">
        <div class="tio-col-extra-tight">
          {{#if this.subtitle}}
            <p class="tio-text-xs-normal tio-fg-muted">{{this.subtitle}}</p>
          {{/if}}
          <div class="tio-cluster gap-1">
            {{#if @listing.isEmployerRecommended}}
              <Chip @appearance="faded" @intent="primary" @size="sm">
                <PhSealCheck class="tio-icon-xs mr-1 inline" />
                {{t "lpn.schools_drawer.employer_recommended"}}
              </Chip>
            {{/if}}
            {{#if this.badges.topBadge}}
              <Chip @appearance="faded" @intent="primary" @size="sm">{{this.badges.topBadge}}</Chip>
            {{/if}}
          </div>
        </div>
      </div>
      <h3 class="tio-text-xl-semibold tio-fg-primary">{{this.view.schoolName}}</h3>
      <div class="tio-text-sm-normal tio-fg-secondary space-y-2">{{this.descriptionHtml}}</div>
      {{#if this.badges.tags.length}}
        <div class="tio-col-tight">
          <p class="tio-text-xs-semibold uppercase tracking-wide tio-fg-muted">
            {{t "lpn.schools_drawer.benefits_heading"}}
          </p>
          <div class="tio-cluster gap-1">
            {{#each this.badges.tags as |label|}}
              <Chip @appearance="faded" @size="sm">{{label}}</Chip>
            {{/each}}
          </div>
        </div>
      {{/if}}
      <p class="tio-text-xs-normal tio-fg-muted">{{t "lpn.schools_drawer.disclaimer"}}</p>
      <div class="tio-cluster gap-2">
        {{#if @onPick}}
          <Button
            @intent="primary"
            @label={{t "lpn.schools_drawer.select"}}
            @isDisabled={{@isPicking}}
            aria-label={{t "lpn.schools_drawer.select_for" school=this.view.schoolName}}
            {{on "click" this.pick}}
            @testHook="lpn-school-detail__select"
            data-test-lpn-school-detail-select
          />
        {{/if}}
        {{#if this.view.url}}
          <Button
            @intent="primary"
            @appearance={{if @onPick "outlined"}}
            @href={{this.view.url}}
            target="_blank"
            rel="noopener noreferrer"
            @label={{t "lpn.schools_drawer.visit_site"}}
            @icon={{PhArrowSquareOut}}
            @iconPlacement="end"
            data-test-lpn-school-detail-visit
          />
        {{/if}}
      </div>
    </div>
  </template>
}
